/**
 * Preview for a comment target
 * Shows the associated photo and the original comment text
 * Uses same rendering as CommentPreview in critique pane for consistency.
 *
 * @module pages/tasks/components/previews/CommentTargetPreview
 */

import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { usePhotoByIdGraphQL } from '@/graphql/hooks/usePhotosGraphQL';
import { AdaptivePhoto } from '@/components/photos/AdaptivePhoto';
import { MarkdownRenderer } from '@/components/MarkdownRenderer';
import { parseCritiqueContent } from '@/utils/critiqueContentParser';
import { GET_CRITIQUE_PREVIEW } from '../../graphql/queries';
import type { TargetPreviewProps, CritiquePreviewData } from '../../types';
import type { Photo } from '@/types/photo';

export function CommentTargetPreview({ task }: TargetPreviewProps) {
  // Check if targetId is a critique ID (cr prefix) - if so, fetch critique data
  const isCritiqueId = task.targetId?.startsWith('cr');

  // Get critiqueId from targetContext if available
  const critiqueIdFromContext = task.targetContext && 'critiqueId' in task.targetContext
    ? String(task.targetContext.critiqueId)
    : null;

  const critiqueIdToFetch = isCritiqueId ? task.targetId : critiqueIdFromContext;

  // Fetch critique data to get the comment content and associated photo
  // CRITICAL: Always provide a placeholder ID when skipping to prevent Apollo Client
  // from validating required variables before checking skip.
  const { data: critiqueData, loading: critiqueLoading } = useQuery<CritiquePreviewData>(
    GET_CRITIQUE_PREVIEW,
    {
      variables: { id: critiqueIdToFetch || 'skip-placeholder' },
      skip: !critiqueIdToFetch,
      fetchPolicy: 'cache-first',
    }
  );

  // If we don't have a critique ID, fetch the photo directly (targetId might be a photo)
  const photoIdToFetch = !isCritiqueId && !critiqueIdFromContext ? task.targetId : undefined;
  const { data: photoData, isLoading: photoLoading } = usePhotoByIdGraphQL(photoIdToFetch);

  const photo = critiqueData?.critique?.photo || photoData;
  const content = critiqueData?.critique?.content;
  const isLoading = critiqueLoading || photoLoading;

  // Parse content to strip markup/edit blocks and extract markup data (same as CommentPreview)
  const parsedContent = useMemo(() => {
    if (!content) return null;
    return parseCritiqueContent(content);
  }, [content]);

  const cleanedContent = parsedContent?.cleanedContent ?? null;
  const markup = parsedContent?.markup;

  return (
    <div className="flex items-start gap-3 p-2 bg-surface-2/50 rounded border border-border/30">
      {/* Photo thumbnail */}
      <div className="w-16 h-16 bg-surface-2 rounded overflow-hidden flex-shrink-0">
        {isLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : photo?.variants ? (
          <AdaptivePhoto
            photo={photo}
            alt={photo.title || 'Photo'}
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </div>

      {/* Comment content - rendered with MarkdownRenderer like CommentPreview */}
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted mb-1">Comment to reply to</div>
        {cleanedContent ? (
          <div className="line-clamp-2">
            <MarkdownRenderer
              content={cleanedContent}
              className="text-foreground text-sm leading-snug break-words"
              photo={photo as Photo}
              markup={markup}
            />
          </div>
        ) : critiqueIdToFetch ? (
          <div className="text-sm text-muted">Loading comment...</div>
        ) : (
          <div className="text-sm text-muted italic">No comment content</div>
        )}
      </div>
    </div>
  );
}
